import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import Sparkline from "@/components/schools/Sparkline";
import { cn } from "@/lib/utils";
import { useCountUp } from "@/hooks/useCountUp";
import KpiInfo from "@/components/schools/KpiInfo";

/** Parses a value like "92%", "1,187", "84" into { num, prefix, suffix } for count-up. */
function parseNumeric(v: string | number) {
  if (typeof v === "number") return { num: v, prefix: "", suffix: "" };
  const m = v.match(/^([^\d\-]*)(-?[\d.,]+)(.*)$/);
  if (!m) return null;
  const num = Number(m[2].replace(/,/g, ""));
  if (!Number.isFinite(num)) return null;
  return { num, prefix: m[1], suffix: m[3] };
}

function AnimatedValue({ value }: { value: string | number }) {
  const parsed = parseNumeric(value);
  const target = parsed?.num ?? 0;
  const animated = useCountUp(target);
  if (!parsed) return <>{value}</>;
  const isInt = Number.isInteger(parsed.num);
  const shown = isInt
    ? Math.round(animated).toLocaleString()
    : animated.toFixed(1);
  return <>{parsed.prefix}{shown}{parsed.suffix}</>;
}

export interface SchoolKpiProps {
  icon?: React.ReactNode;
  label: string;
  value: string | number;
  hint?: string;
  tone?: "positive" | "warning" | "neutral";
  series?: number[];
  /** Optional peer-school benchmark shown as "vs. peers" pill. */
  benchmark?: number;
  /** Suffix appended to the benchmark delta (e.g. "%", "pts"). */
  benchmarkSuffix?: string;
  /** Signature deep-green filled treatment for the hero/north-star KPI. */
  variant?: "default" | "north-star";
  /** Glossary key — renders an info popover with the KPI definition. */
  infoKey?: string;
}

const toneColor = {
  positive: "text-emerald-500",
  warning:  "text-amber-500",
  neutral:  "text-primary",
} as const;

/**
 * Shared executive-style KPI card for WeKIT Education Intelligence™ dashboards.
 * Uses semantic tokens and inherits `currentColor` for the sparkline.
 */
export default function SchoolKpi({ icon, label, value, hint, tone = "neutral", series, benchmark, benchmarkSuffix = "", variant = "default", infoKey }: SchoolKpiProps) {
  const trending =
    series && series.length > 1
      ? series[series.length - 1] >= series[0]
        ? "up"
        : "down"
      : null;

  const parsedValue = parseNumeric(value);
  const delta =
    benchmark != null && parsedValue ? parsedValue.num - benchmark : null;

  const isNorthStar = variant === "north-star";

  return (
    <Card
      className={cn(
        "border-0 ring-1 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all",
        isNorthStar
          ? "schools-north-star ring-transparent"
          : "ring-border bg-gradient-to-br from-card via-card to-primary/5"
      )}
    >
      <CardContent className="pt-6">
        <div className={cn("flex items-center gap-2 mb-2", isNorthStar ? "opacity-80" : "text-muted-foreground")}>
          {icon && (
            <span
              className={cn(
                "w-6 h-6 rounded-lg grid place-items-center",
                isNorthStar ? "bg-white/15 text-primary-foreground" : "bg-primary/10 text-primary"
              )}
            >
              {icon}
            </span>
          )}
          <span className="schools-eyebrow flex-1" style={isNorthStar ? { color: "inherit" } : undefined}>{label}</span>
          {infoKey && <KpiInfo glossaryKey={infoKey} invert={isNorthStar} />}
        </div>
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-semibold tracking-tight tabular-nums"><AnimatedValue value={value} /></div>
              {trending === "up" && <TrendingUp className={cn("w-4 h-4", isNorthStar ? "text-emerald-200" : "text-emerald-500")} />}
              {trending === "down" && <TrendingDown className={cn("w-4 h-4", isNorthStar ? "text-rose-200" : "text-rose-500")} />}
            </div>
            {hint && <div className={cn("text-xs mt-1.5", isNorthStar ? "opacity-80" : "text-muted-foreground")}>{hint}</div>}
            {delta != null && (
              <div
                className={cn(
                  "mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium",
                  isNorthStar ? "bg-white/15" : "bg-muted/60 text-muted-foreground"
                )}
              >
                <span>vs. peers</span>
                <span className={cn("tabular-nums", isNorthStar ? "" : (delta >= 0 ? "text-emerald-600" : "text-rose-600"))}>
                  {delta >= 0 ? "+" : ""}{Number.isInteger(delta) ? delta : delta.toFixed(1)}{benchmarkSuffix}
                </span>
              </div>
            )}
          </div>
          {series && (
            <Sparkline data={series} className={cn(isNorthStar ? "text-primary-foreground" : toneColor[tone])} width={72} height={28} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
